/**
 * AdminGlobalDictionaries — panel ITE „Globalne słowniki".
 *
 * Centralny katalog słowników zarządzany przez administratorów ITE (mirror
 * `AdminGlobalDocumentTypes`, patrz `docs/dictionaries-feature-brief.md` §2).
 * Master-detail: lewa kolumna — lista słowników (status, sortOrder), prawa
 * kolumna — pozycje wybranego słownika.
 *
 * Cykl życia: szkic → opublikowany → wycofany (bez hard-delete po publikacji;
 * szkic można skasować miękko). Kod jest niezmienialny po publikacji — backend
 * odrzuca zmianę 409 CODE_IMMUTABLE, modal blokuje edycję pola z podpowiedzią.
 *
 * Mutacje gatowane `hasPermission('global_dictionaries.manage')` — bez tego
 * uprawnienia user widzi tylko listę + pozycje (GET wymaga `.view`).
 *
 * Endpoint: /api/admin/global-dictionaries.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Pencil, Plus, RotateCcw, Send, Trash2, Undo2 } from 'lucide-react';

import { Badge, type BadgeColor } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ExternalSourceInfoPanel } from '@/components/dictionaries/ExternalSourceInfoPanel';
import {
  emptyGlobalDictionaryFormValues,
  GlobalDictionaryFormModal,
} from '@/components/dictionaries/GlobalDictionaryFormModal';
import {
  emptyGlobalDictionaryItemFormValues,
  GlobalDictionaryItemFormModal,
} from '@/components/dictionaries/GlobalDictionaryItemFormModal';
import { Layout } from '@/components/Layout';
import { SearchInput } from '@/components/SearchInput';
import { Skeleton } from '@/components/Skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import {
  type AdminGlobalDictionary,
  type GlobalDictionaryFormValues,
  type GlobalDictionaryItem,
  type GlobalDictionaryItemFormValues,
  type GlobalDictionaryStatus,
  useAddGlobalDictionaryItem,
  useCreateGlobalDictionary,
  useDeleteGlobalDictionary,
  useDeleteGlobalDictionaryItem,
  useDictionarySources,
  useGlobalDictionaries,
  useGlobalDictionaryDetail,
  usePublishGlobalDictionary,
  useRepublishGlobalDictionary,
  useRestoreGlobalDictionary,
  useUpdateGlobalDictionary,
  useUpdateGlobalDictionaryItem,
  useWithdrawGlobalDictionary,
} from '@/lib/global-dictionaries/api';

const MANAGE_PERMISSION = 'global_dictionaries.manage';
const VIEW_PERMISSION = 'global_dictionaries.view';

const STATUS_COLOR: Record<GlobalDictionaryStatus, BadgeColor> = {
  draft: 'gray',
  published: 'green',
  withdrawn: 'amber',
};

type ApiErrorCode =
  | 'CODE_IMMUTABLE'
  | 'INVALID_STATUS'
  | 'CANNOT_DELETE_PUBLISHED'
  | 'CODE_TAKEN'
  | 'NAME_TAKEN'
  | 'VALIDATION_FAILED';

function extractErrorCode(err: unknown): ApiErrorCode | null {
  if (!isAxiosError(err)) return null;
  return (err.response?.data?.error as ApiErrorCode | undefined) ?? null;
}

function toFormValues(dict: AdminGlobalDictionary): GlobalDictionaryFormValues {
  return {
    code: dict.code,
    name: dict.name,
    description: dict.description ?? '',
    displayMode: dict.displayMode,
    sortOrder: String(dict.sortOrder),
    sourceKind: dict.sourceKind,
    sourceKey: dict.sourceKey,
    sourceSql: dict.sourceSql ?? null,
    sourceParams: dict.sourceParams ?? [],
  };
}

function toItemFormValues(item: GlobalDictionaryItem): GlobalDictionaryItemFormValues {
  return {
    code: item.code,
    label: item.label,
    sortOrder: item.sortOrder !== null ? String(item.sortOrder) : '',
    color: item.color ?? '',
    isActive: item.isActive,
  };
}

export function AdminGlobalDictionaries() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const toast = useToast();

  const canView = hasPermission(VIEW_PERMISSION);
  const canManage = hasPermission(MANAGE_PERMISSION);

  const [includeDeleted, setIncludeDeleted] = useState(false);
  // Wyszukiwarki (parytet z tenantowym „Słowniki"): lewa filtruje listę słowników po
  // kodzie/nazwie/statusie, prawa filtruje pozycje wybranego słownika po kodzie/etykiecie.
  const [search, setSearch] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<AdminGlobalDictionary | null>(null);
  const [formServerErrors, setFormServerErrors] = useState<Record<string, string>>({});
  const [pendingPublish, setPendingPublish] = useState<AdminGlobalDictionary | null>(null);
  const [pendingWithdraw, setPendingWithdraw] = useState<AdminGlobalDictionary | null>(null);
  const [pendingRepublish, setPendingRepublish] = useState<AdminGlobalDictionary | null>(null);
  const [pendingRestore, setPendingRestore] = useState<AdminGlobalDictionary | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AdminGlobalDictionary | null>(null);

  const [itemFormOpen, setItemFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<GlobalDictionaryItem | null>(null);
  const [itemServerErrors, setItemServerErrors] = useState<Record<string, string>>({});
  const [pendingDeleteItem, setPendingDeleteItem] = useState<GlobalDictionaryItem | null>(null);

  const listQ = useGlobalDictionaries(includeDeleted);
  const detailQ = useGlobalDictionaryDetail(selectedId);
  // Etap 10.5 — rejestr źródeł zewnętrznych, zasila panel informacyjny (badge listy +
  // ExternalSourceInfoPanel zamiast tabeli pozycji dla sourceKind==='external').
  const sourcesQ = useDictionarySources();
  const externalSources = sourcesQ.data ?? [];

  const createMutation = useCreateGlobalDictionary();
  const updateMutation = useUpdateGlobalDictionary();
  const publishMutation = usePublishGlobalDictionary();
  const withdrawMutation = useWithdrawGlobalDictionary();
  const republishMutation = useRepublishGlobalDictionary();
  const restoreMutation = useRestoreGlobalDictionary();
  const deleteMutation = useDeleteGlobalDictionary();

  const addItemMutation = useAddGlobalDictionaryItem(selectedId ?? '');
  const updateItemMutation = useUpdateGlobalDictionaryItem(selectedId ?? '');
  const deleteItemMutation = useDeleteGlobalDictionaryItem(selectedId ?? '');

  // Auto-zaznacz pierwszy słownik, gdy nic nie wybrano — prawy panel od razu pełny
  // (bez „kliknij, żeby zobaczyć pozycje"). Gdy wybrany zniknie z listy (kasowanie,
  // przełączenie „pokaż usunięte"), znów wskakuje pierwszy dostępny.
  useEffect(() => {
    const list = listQ.data ?? [];
    const first = list[0];
    if (!first) return;
    if (selectedId === null || !list.some((d) => d.id === selectedId)) {
      setSelectedId(first.id);
    }
  }, [listQ.data, selectedId]);

  function handleCreateSubmit(values: GlobalDictionaryFormValues) {
    setFormServerErrors({});
    createMutation.mutate(values, {
      onSuccess: () => {
        toast.show({ variant: 'success', title: t('admin.globalDictionaries.toast.created') });
        setCreateOpen(false);
      },
      onError: (err) => {
        const code = extractErrorCode(err);
        if (code === 'CODE_TAKEN') {
          setFormServerErrors({ code: t('admin.globalDictionaries.toast.codeTaken') });
          return;
        }
        if (code === 'NAME_TAKEN') {
          setFormServerErrors({ name: t('admin.globalDictionaries.toast.nameTaken') });
          return;
        }
        toast.show({ variant: 'error', title: t('admin.globalDictionaries.toast.createError') });
      },
    });
  }

  function handleUpdateSubmit(values: GlobalDictionaryFormValues) {
    if (!editing) return;
    setFormServerErrors({});
    updateMutation.mutate(
      { id: editing.id, values, original: editing },
      {
        onSuccess: () => {
          toast.show({ variant: 'success', title: t('admin.globalDictionaries.toast.updated') });
          setEditing(null);
        },
        onError: (err) => {
          const code = extractErrorCode(err);
          if (code === 'CODE_TAKEN') {
            setFormServerErrors({ code: t('admin.globalDictionaries.toast.codeTaken') });
            return;
          }
          if (code === 'NAME_TAKEN') {
            setFormServerErrors({ name: t('admin.globalDictionaries.toast.nameTaken') });
            return;
          }
          if (code === 'CODE_IMMUTABLE') {
            toast.show({
              variant: 'warning',
              title: t('admin.globalDictionaries.toast.codeImmutable'),
            });
            return;
          }
          toast.show({ variant: 'error', title: t('admin.globalDictionaries.toast.updateError') });
        },
      },
    );
  }

  function handlePublish() {
    if (!pendingPublish) return;
    publishMutation.mutate(pendingPublish.id, {
      onSuccess: () => {
        toast.show({ variant: 'success', title: t('admin.globalDictionaries.toast.published') });
        setPendingPublish(null);
      },
      onError: (err) => {
        const code = extractErrorCode(err);
        toast.show({
          variant: code === 'INVALID_STATUS' ? 'warning' : 'error',
          title:
            code === 'INVALID_STATUS'
              ? t('admin.globalDictionaries.toast.invalidStatus')
              : t('admin.globalDictionaries.toast.publishError'),
        });
        setPendingPublish(null);
      },
    });
  }

  function handleWithdraw() {
    if (!pendingWithdraw) return;
    withdrawMutation.mutate(pendingWithdraw.id, {
      onSuccess: () => {
        toast.show({ variant: 'success', title: t('admin.globalDictionaries.toast.withdrawn') });
        setPendingWithdraw(null);
      },
      onError: (err) => {
        const code = extractErrorCode(err);
        toast.show({
          variant: code === 'INVALID_STATUS' ? 'warning' : 'error',
          title:
            code === 'INVALID_STATUS'
              ? t('admin.globalDictionaries.toast.invalidStatus')
              : t('admin.globalDictionaries.toast.withdrawError'),
        });
        setPendingWithdraw(null);
      },
    });
  }

  function handleRepublish() {
    if (!pendingRepublish) return;
    republishMutation.mutate(pendingRepublish.id, {
      onSuccess: () => {
        toast.show({ variant: 'success', title: t('admin.globalDictionaries.toast.republished') });
        setPendingRepublish(null);
      },
      onError: (err) => {
        const code = extractErrorCode(err);
        toast.show({
          variant: code === 'INVALID_STATUS' ? 'warning' : 'error',
          title:
            code === 'INVALID_STATUS'
              ? t('admin.globalDictionaries.toast.invalidStatus')
              : t('admin.globalDictionaries.toast.republishError'),
        });
        setPendingRepublish(null);
      },
    });
  }

  function handleRestore() {
    if (!pendingRestore) return;
    restoreMutation.mutate(pendingRestore.id, {
      onSuccess: () => {
        toast.show({ variant: 'success', title: t('admin.globalDictionaries.toast.restored') });
        setPendingRestore(null);
      },
      onError: (err) => {
        const code = extractErrorCode(err);
        toast.show({
          variant: code === 'INVALID_STATUS' ? 'warning' : 'error',
          title:
            code === 'INVALID_STATUS'
              ? t('admin.globalDictionaries.toast.invalidStatus')
              : t('admin.globalDictionaries.toast.restoreError'),
        });
        setPendingRestore(null);
      },
    });
  }

  function handleDelete() {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete.id, {
      onSuccess: () => {
        toast.show({ variant: 'success', title: t('admin.globalDictionaries.toast.deleted') });
        if (selectedId === pendingDelete.id) setSelectedId(null);
        setPendingDelete(null);
      },
      onError: (err) => {
        const code = extractErrorCode(err);
        toast.show({
          variant: code === 'CANNOT_DELETE_PUBLISHED' ? 'warning' : 'error',
          title:
            code === 'CANNOT_DELETE_PUBLISHED'
              ? t('admin.globalDictionaries.toast.cannotDeletePublished')
              : t('admin.globalDictionaries.toast.deleteError'),
        });
        setPendingDelete(null);
      },
    });
  }

  function handleAddItemSubmit(values: GlobalDictionaryItemFormValues) {
    setItemServerErrors({});
    addItemMutation.mutate(values, {
      onSuccess: () => {
        toast.show({ variant: 'success', title: t('admin.globalDictionaries.toast.itemCreated') });
        setItemFormOpen(false);
      },
      onError: (err) => {
        const code = extractErrorCode(err);
        if (code === 'CODE_TAKEN') {
          setItemServerErrors({ code: t('admin.globalDictionaries.toast.itemCodeTaken') });
          return;
        }
        toast.show({
          variant: 'error',
          title: t('admin.globalDictionaries.toast.itemCreateError'),
        });
      },
    });
  }

  function handleUpdateItemSubmit(values: GlobalDictionaryItemFormValues) {
    if (!editingItem) return;
    setItemServerErrors({});
    updateItemMutation.mutate(
      { itemId: editingItem.id, values, original: editingItem },
      {
        onSuccess: () => {
          toast.show({
            variant: 'success',
            title: t('admin.globalDictionaries.toast.itemUpdated'),
          });
          setEditingItem(null);
        },
        onError: () => {
          toast.show({
            variant: 'error',
            title: t('admin.globalDictionaries.toast.itemUpdateError'),
          });
        },
      },
    );
  }

  function handleDeleteItem() {
    if (!pendingDeleteItem) return;
    deleteItemMutation.mutate(pendingDeleteItem.id, {
      onSuccess: () => {
        toast.show({
          variant: 'success',
          title: t('admin.globalDictionaries.toast.itemDeleted'),
        });
        setPendingDeleteItem(null);
      },
      onError: () => {
        toast.show({
          variant: 'error',
          title: t('admin.globalDictionaries.toast.itemDeleteError'),
        });
        setPendingDeleteItem(null);
      },
    });
  }

  if (!canView) {
    return (
      <Layout>
        <section className="px-6 py-8">
          <div
            role="alert"
            className="rounded border border-red-300 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300"
          >
            {t('admin.globalDictionaries.noPermission')}
          </div>
        </section>
      </Layout>
    );
  }

  const dictionaries = listQ.data ?? [];
  const dq = search.trim().toLowerCase();
  const filteredDictionaries = dq
    ? dictionaries.filter(
        (d) =>
          d.code.toLowerCase().includes(dq) ||
          d.name.toLowerCase().includes(dq) ||
          t(`admin.globalDictionaries.status.${d.status}`).toLowerCase().includes(dq),
      )
    : dictionaries;
  const detail = detailQ.data ?? null;
  const items = detail?.items ?? [];
  const iq = itemSearch.trim().toLowerCase();
  const filteredItems = iq
    ? items.filter(
        (it) => it.code.toLowerCase().includes(iq) || it.label.toLowerCase().includes(iq),
      )
    : items;

  return (
    <Layout>
      <section className="px-6 py-8">
        <header className="mb-6 flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">{t('admin.globalDictionaries.title')}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('admin.globalDictionaries.subtitle')}
            </p>
          </div>
          {canManage && (
            <Button
              variant="primary"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => {
                setFormServerErrors({});
                setCreateOpen(true);
              }}
              data-testid="global-dictionary-new-btn"
            >
              {t('admin.globalDictionaries.newDictionary')}
            </Button>
          )}
        </header>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
          {/* Lewa kolumna — lista słowników */}
          <div>
            <div className="mb-3 flex h-9 items-center justify-between">
              <h2 className="text-lg font-semibold text-text-primary">
                {t('admin.globalDictionaries.listTitle')}
              </h2>
              <label className="flex items-center gap-2 text-sm text-text-secondary">
                <input
                  type="checkbox"
                  checked={includeDeleted}
                  onChange={(e) => setIncludeDeleted(e.target.checked)}
                  data-testid="global-dictionary-include-deleted"
                />
                {t('admin.globalDictionaries.showDeleted')}
              </label>
            </div>

            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder={t('admin.globalDictionaries.searchPlaceholder')}
              className="mb-3"
              data-testid="global-dictionary-search"
            />

            {listQ.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : listQ.isError ? (
              <div
                role="alert"
                className="rounded border border-red-300 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300"
              >
                {isAxiosError(listQ.error) && listQ.error.response?.status === 403
                  ? t('admin.globalDictionaries.noPermission')
                  : t('admin.globalDictionaries.loadError')}
              </div>
            ) : dictionaries.length === 0 ? (
              <Card padding="md">
                <p className="text-sm text-text-tertiary">{t('admin.globalDictionaries.empty')}</p>
              </Card>
            ) : (
              <Card padding="none">
                <ul className="divide-y divide-border-primary" data-testid="global-dictionary-list">
                  {filteredDictionaries.length === 0 && (
                    <li
                      className="px-4 py-6 text-center text-sm text-text-tertiary"
                      data-testid="global-dictionary-no-matches"
                    >
                      {t('admin.globalDictionaries.noMatches')}
                    </li>
                  )}
                  {filteredDictionaries.map((dict) => (
                    <li key={dict.id}>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedId(dict.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            setSelectedId(dict.id);
                          } else if (e.key === ' ') {
                            e.preventDefault();
                            setSelectedId(dict.id);
                          }
                        }}
                        data-testid={`global-dictionary-row-${dict.id}`}
                        aria-current={selectedId === dict.id ? 'true' : undefined}
                        className={
                          'flex w-full items-center justify-between gap-2 border-l-[3px] px-4 py-3 text-left transition ' +
                          (selectedId === dict.id
                            ? 'border-accent-500 bg-accent-50 dark:bg-accent-500/10'
                            : 'border-transparent hover:bg-bg-tertiary/60')
                        }
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <code className="rounded bg-bg-secondary px-1.5 py-0.5 text-xs text-text-secondary">
                              {dict.code}
                            </code>
                            <span
                              className={
                                'truncate font-medium ' +
                                (selectedId === dict.id
                                  ? 'font-semibold text-accent-700 dark:text-accent-300'
                                  : 'text-text-primary')
                              }
                            >
                              {dict.name}
                            </span>
                          </div>
                          <div className="mt-1 flex items-center gap-2">
                            <Badge color={STATUS_COLOR[dict.status]}>
                              {t(`admin.globalDictionaries.status.${dict.status}`)}
                            </Badge>
                            {dict.sourceKind === 'external' && dict.sourceKey && (
                              <Badge
                                color="blue"
                                data-testid={`global-dictionary-source-badge-${dict.id}`}
                              >
                                {t('admin.globalDictionaries.columns.source')}:{' '}
                                {(() => {
                                  const source = externalSources.find(
                                    (s) => s.key === dict.sourceKey,
                                  );
                                  return source ? t(source.nameI18nKey) : dict.sourceKey;
                                })()}
                              </Badge>
                            )}
                            {/* Faza 2d (ADR-0068): słownik zewnętrzny definiowany zapytaniem
                                SQL (XOR z sourceKey) — badge widoczny, gdy serializer admina
                                wystawia `sourceSql` w liście. */}
                            {dict.sourceKind === 'external' && dict.sourceSql != null && (
                              <Badge
                                color="accent"
                                data-testid={`global-dictionary-sql-badge-${dict.id}`}
                              >
                                {t('admin.globalDictionaries.sqlBadge')}
                              </Badge>
                            )}
                            <span className="text-xs text-text-muted">
                              {t('admin.globalDictionaries.columns.sortOrder')}: {dict.sortOrder}
                            </span>
                          </div>
                        </div>
                        {canManage && (
                          <div
                            className="flex shrink-0 items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                setFormServerErrors({});
                                setEditing(dict);
                              }}
                              aria-label={t('admin.globalDictionaries.actions.edit')}
                              className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-text-primary"
                              data-testid={`global-dictionary-edit-${dict.id}`}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            {dict.status === 'draft' && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => setPendingPublish(dict)}
                                  aria-label={t('admin.globalDictionaries.actions.publish')}
                                  className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-accent-600"
                                  data-testid={`global-dictionary-publish-${dict.id}`}
                                >
                                  <Send className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setPendingDelete(dict)}
                                  aria-label={t('admin.globalDictionaries.actions.delete')}
                                  className="rounded p-1.5 text-text-muted transition hover:bg-error-500/10 hover:text-error-500"
                                  data-testid={`global-dictionary-delete-${dict.id}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </>
                            )}
                            {dict.status === 'published' && (
                              <button
                                type="button"
                                onClick={() => setPendingWithdraw(dict)}
                                aria-label={t('admin.globalDictionaries.actions.withdraw')}
                                className="rounded p-1.5 text-text-muted transition hover:bg-error-500/10 hover:text-error-500"
                                data-testid={`global-dictionary-withdraw-${dict.id}`}
                              >
                                <Undo2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {dict.status === 'withdrawn' && (
                              <button
                                type="button"
                                onClick={() => setPendingRepublish(dict)}
                                aria-label={t('admin.globalDictionaries.actions.republish')}
                                className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-accent-600"
                                data-testid={`global-dictionary-republish-${dict.id}`}
                              >
                                <Send className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {dict.gcrecord != null && (
                              <button
                                type="button"
                                onClick={() => setPendingRestore(dict)}
                                aria-label={t('admin.globalDictionaries.actions.restore')}
                                className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-text-primary"
                                data-testid={`global-dictionary-restore-${dict.id}`}
                              >
                                <RotateCcw className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>

          {/* Prawa kolumna — pozycje wybranego słownika */}
          <div>
            {!selectedId ? (
              <Card padding="md">
                <p className="text-sm text-text-tertiary">
                  {t('admin.globalDictionaries.selectPrompt')}
                </p>
              </Card>
            ) : detailQ.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : detailQ.isError || !detail ? (
              <div
                role="alert"
                className="rounded border border-red-300 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300"
              >
                {t('admin.globalDictionaries.detailLoadError')}
              </div>
            ) : (
              <>
                <div className="mb-3 flex h-9 items-center justify-between">
                  <h2 className="text-lg font-semibold text-text-primary">
                    {t('admin.globalDictionaries.itemsTitle', { name: detail.name })}
                  </h2>
                  {canManage && detail.sourceKind !== 'external' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<Plus className="h-3.5 w-3.5" />}
                      onClick={() => {
                        setItemServerErrors({});
                        setItemFormOpen(true);
                      }}
                      data-testid="global-dictionary-item-new-btn"
                    >
                      {t('admin.globalDictionaries.newItem')}
                    </Button>
                  )}
                </div>

                {detail.sourceKind !== 'external' && items.length > 0 && (
                  <SearchInput
                    value={itemSearch}
                    onChange={setItemSearch}
                    placeholder={t('admin.globalDictionaries.itemSearchPlaceholder')}
                    className="mb-3"
                    data-testid="global-dictionary-items-search"
                  />
                )}

                {detail.sourceKind === 'external' && detail.sourceSql != null ? (
                  /* Faza 2d (ADR-0068): źródło SQL — panel informacyjny zamiast tabeli
                     pozycji (pozycje żyją w bazie tenanta; podgląd przez „Testuj" w modalu). */
                  <div
                    className="space-y-3 rounded-lg border border-border-primary bg-bg-secondary p-3"
                    data-testid="global-dictionary-sql-info-panel"
                  >
                    <p className="text-sm font-semibold text-text-primary">
                      {t('admin.globalDictionaries.sqlInfoTitle')}
                    </p>
                    <pre className="overflow-x-auto rounded-md bg-bg-tertiary p-2 font-mono text-xs text-text-secondary">
                      {detail.sourceSql}
                    </pre>
                    {(detail.sourceParams ?? []).length > 0 && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                          {t('admin.globalDictionaries.form.sourceParamsTitle')}
                        </p>
                        <ul className="mt-1.5 space-y-1">
                          {(detail.sourceParams ?? []).map((param) => (
                            <li
                              key={param.name}
                              className="flex items-center justify-between gap-2 text-sm text-text-secondary"
                            >
                              <span>
                                <code className="rounded bg-bg-tertiary px-1 py-0.5 text-xs">
                                  {param.name}
                                </code>{' '}
                                — {t(`admin.globalDictionaries.form.paramType.${param.type}`)}
                              </span>
                              <span className="shrink-0 text-xs text-text-muted">
                                {param.required
                                  ? t('admin.globalDictionaries.form.paramRequired')
                                  : t('admin.globalDictionaries.form.paramOptional')}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <p className="text-xs italic text-text-tertiary">
                      {t('admin.globalDictionaries.form.externalNoPreview')}
                    </p>
                  </div>
                ) : detail.sourceKind === 'external' ? (
                  <ExternalSourceInfoPanel
                    source={externalSources.find((s) => s.key === detail.sourceKey) ?? null}
                    sourceKey={detail.sourceKey}
                  />
                ) : items.length === 0 ? (
                  <Card padding="md">
                    <p className="text-sm text-text-tertiary">
                      {t('admin.globalDictionaries.itemsEmpty')}
                    </p>
                  </Card>
                ) : (
                  <Card padding="none">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="global-dictionary-items-table">
                        <thead className="border-b border-border-primary bg-bg-secondary text-xs uppercase tracking-wider text-text-muted">
                          <tr>
                            <th className="px-4 py-2 text-left">
                              {t('admin.globalDictionaries.columns.itemCode')}
                            </th>
                            <th className="px-4 py-2 text-left">
                              {t('admin.globalDictionaries.columns.itemLabel')}
                            </th>
                            <th className="px-4 py-2 text-left">
                              {t('admin.globalDictionaries.columns.itemActive')}
                            </th>
                            <th className="px-4 py-2 text-left">
                              {t('admin.globalDictionaries.columns.sortOrder')}
                            </th>
                            {canManage && (
                              <th className="px-4 py-2 text-right">
                                {t('admin.globalDictionaries.columns.actions')}
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border-primary">
                          {filteredItems.length === 0 && (
                            <tr>
                              <td
                                colSpan={canManage ? 5 : 4}
                                className="px-4 py-6 text-center text-sm text-text-tertiary"
                                data-testid="global-dictionary-items-no-matches"
                              >
                                {t('admin.globalDictionaries.itemsNoMatches')}
                              </td>
                            </tr>
                          )}
                          {filteredItems.map((item) => (
                            <tr key={item.id} data-testid={`global-dictionary-item-row-${item.id}`}>
                              <td className="px-4 py-3">
                                <code className="rounded bg-bg-secondary px-1.5 py-0.5 text-xs text-text-secondary">
                                  {item.code}
                                </code>
                              </td>
                              <td className="px-4 py-3 font-medium text-text-primary">
                                {item.color && (
                                  <span
                                    className="mr-2 inline-block h-2.5 w-2.5 rounded-full align-middle"
                                    style={{ backgroundColor: item.color }}
                                    aria-hidden="true"
                                  />
                                )}
                                {item.label}
                              </td>
                              <td className="px-4 py-3">
                                <Badge color={item.isActive ? 'green' : 'gray'}>
                                  {item.isActive
                                    ? t('admin.globalDictionaries.itemActive.active')
                                    : t('admin.globalDictionaries.itemActive.inactive')}
                                </Badge>
                              </td>
                              <td className="px-4 py-3 text-text-secondary">
                                {item.sortOrder ?? <span className="text-text-muted">—</span>}
                              </td>
                              {canManage && (
                                <td className="px-4 py-3">
                                  <div className="flex items-center justify-end gap-1">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setItemServerErrors({});
                                        setEditingItem(item);
                                      }}
                                      aria-label={t('admin.globalDictionaries.actions.edit')}
                                      className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-text-primary"
                                      data-testid={`global-dictionary-item-edit-${item.id}`}
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                    </button>
                                    {/* Po PUBLIKACJI słownika pozycji nie wolno usuwać —
                                        żyją u tenantów, a ich rekordy trzymają kody
                                        (backend odmawia 409 PUBLISHED_ITEM_CANNOT_DELETE).
                                        Wycofanie pojęcia bez utraty danych: edycja →
                                        „nieaktywna". Dlatego kosza tu nie ma, zamiast
                                        pokazywać przycisk, który zawsze zawiedzie. */}
                                    {detail?.status !== 'published' && (
                                      <button
                                        type="button"
                                        onClick={() => setPendingDeleteItem(item)}
                                        aria-label={t('admin.globalDictionaries.actions.delete')}
                                        className="rounded p-1.5 text-text-muted transition hover:bg-error-500/10 hover:text-error-500"
                                        data-testid={`global-dictionary-item-delete-${item.id}`}
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Modal create / edit słownika */}
      <GlobalDictionaryFormModal
        open={createOpen || editing !== null}
        mode={editing ? 'edit' : 'create'}
        initialValues={editing ? toFormValues(editing) : emptyGlobalDictionaryFormValues()}
        codeLocked={editing ? editing.status !== 'draft' : false}
        saving={createMutation.isPending || updateMutation.isPending}
        serverErrors={formServerErrors}
        onSubmit={(values) => {
          if (editing) {
            handleUpdateSubmit(values);
          } else {
            handleCreateSubmit(values);
          }
        }}
        onClose={() => {
          setCreateOpen(false);
          setEditing(null);
          setFormServerErrors({});
        }}
      />

      {/* Modal add / edit pozycji */}
      <GlobalDictionaryItemFormModal
        open={itemFormOpen || editingItem !== null}
        mode={editingItem ? 'edit' : 'create'}
        initialValues={
          editingItem ? toItemFormValues(editingItem) : emptyGlobalDictionaryItemFormValues()
        }
        saving={addItemMutation.isPending || updateItemMutation.isPending}
        serverErrors={itemServerErrors}
        onSubmit={(values) => {
          if (editingItem) {
            handleUpdateItemSubmit(values);
          } else {
            handleAddItemSubmit(values);
          }
        }}
        onClose={() => {
          setItemFormOpen(false);
          setEditingItem(null);
          setItemServerErrors({});
        }}
      />

      {/* Potwierdzenie publikacji — nie-destrukcyjne (variant primary) */}
      <ConfirmDialog
        open={pendingPublish !== null}
        title={t('admin.globalDictionaries.confirmPublish.title')}
        message={
          pendingPublish
            ? t('admin.globalDictionaries.confirmPublish.message', {
                name: pendingPublish.name,
                code: pendingPublish.code,
              })
            : ''
        }
        confirmLabel={t('admin.globalDictionaries.confirmPublish.confirm')}
        variant="primary"
        onConfirm={handlePublish}
        onClose={() => setPendingPublish(null)}
      />

      {/* Potwierdzenie wycofania — destrukcyjne dla widoczności u tenantów (variant danger) */}
      <ConfirmDialog
        open={pendingWithdraw !== null}
        title={t('admin.globalDictionaries.confirmWithdraw.title')}
        message={
          pendingWithdraw
            ? t('admin.globalDictionaries.confirmWithdraw.message', {
                name: pendingWithdraw.name,
                code: pendingWithdraw.code,
              })
            : ''
        }
        confirmLabel={t('admin.globalDictionaries.confirmWithdraw.confirm')}
        variant="danger"
        onConfirm={handleWithdraw}
        onClose={() => setPendingWithdraw(null)}
      />

      {/* Potwierdzenie ponownej publikacji (powrót z wycofania) — nie-destrukcyjne (variant primary) */}
      <ConfirmDialog
        open={pendingRepublish !== null}
        title={t('admin.globalDictionaries.confirmRepublish.title')}
        message={
          pendingRepublish
            ? t('admin.globalDictionaries.confirmRepublish.message', {
                name: pendingRepublish.name,
                code: pendingRepublish.code,
              })
            : ''
        }
        confirmLabel={t('admin.globalDictionaries.confirmRepublish.confirm')}
        variant="primary"
        onConfirm={handleRepublish}
        onClose={() => setPendingRepublish(null)}
      />

      {/* Potwierdzenie przywrócenia — nie-destrukcyjne (variant primary) */}
      <ConfirmDialog
        open={pendingRestore !== null}
        title={t('admin.globalDictionaries.confirmRestore.title')}
        message={
          pendingRestore
            ? t('admin.globalDictionaries.confirmRestore.message', {
                name: pendingRestore.name,
                code: pendingRestore.code,
              })
            : ''
        }
        confirmLabel={t('admin.globalDictionaries.confirmRestore.confirm')}
        variant="primary"
        onConfirm={handleRestore}
        onClose={() => setPendingRestore(null)}
      />

      {/* Potwierdzenie usunięcia szkicu — destrukcyjne (variant danger) */}
      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('admin.globalDictionaries.confirmDelete.title')}
        message={
          pendingDelete
            ? t('admin.globalDictionaries.confirmDelete.message', {
                name: pendingDelete.name,
                code: pendingDelete.code,
              })
            : ''
        }
        confirmLabel={t('admin.globalDictionaries.confirmDelete.confirm')}
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setPendingDelete(null)}
      />

      {/* Potwierdzenie usunięcia pozycji — destrukcyjne (variant danger) */}
      <ConfirmDialog
        open={pendingDeleteItem !== null}
        title={t('admin.globalDictionaries.confirmDeleteItem.title')}
        message={
          pendingDeleteItem
            ? t('admin.globalDictionaries.confirmDeleteItem.message', {
                label: pendingDeleteItem.label,
                code: pendingDeleteItem.code,
              })
            : ''
        }
        confirmLabel={t('admin.globalDictionaries.confirmDeleteItem.confirm')}
        variant="danger"
        onConfirm={handleDeleteItem}
        onClose={() => setPendingDeleteItem(null)}
      />
    </Layout>
  );
}
